// src/components/DealerRegistration.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { PublicPage, Eyebrow } from './public/PublicLayout';

const DealerRegistration = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    // Business Info
    businessName: '',
    dealerLicenseNumber: '',
    ein: '',
    
    // Contact Info
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    contactName: '',
    
    // Address
    address: '',
    city: '',
    state: '',
    zip: '',
    
    // Agreement
    agreeToTerms: false,
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateStep1 = () => {
    if (!formData.businessName.trim()) {
      setError('Business name is required');
      return false;
    }
    if (!formData.dealerLicenseNumber.trim()) {
      setError('Dealer license number is required');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!formData.password || formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (!formData.phone.trim()) {
      setError('Phone number is required');
      return false;
    }
    if (!formData.contactName.trim()) {
      setError('Contact name is required');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!formData.city.trim() || !formData.state.trim() || !formData.zip.trim()) {
      setError('City, State, and ZIP are required');
      return false;
    }
    if (!formData.agreeToTerms) {
      setError('You must agree to the terms and conditions');
      return false;
    }
    return true;
  };

  const nextStep = () => {
    setError('');
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  const prevStep = () => {
    setError('');
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!validateStep3()) return;
    
    setIsLoading(true);
    
    try {
      // Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        formData.email, 
        formData.password
      );
      
      const userId = userCredential.user.uid;

// Create user document FIRST (for role-based access)
await setDoc(doc(db, 'users', userId), {
  email: formData.email.toLowerCase().trim(),
  role: 'dealer',
  dealerId: userId,
  active: true,
  createdAt: serverTimestamp(),
  updatedAt: serverTimestamp(),
});

// THEN create dealer document
await setDoc(doc(db, 'dealers', userId), {
  businessName: formData.businessName.trim(),
  dealerLicenseNumber: formData.dealerLicenseNumber.trim(),
  ein: formData.ein.trim() || null,
  
  email: formData.email.toLowerCase().trim(),
  phone: formData.phone.trim(),
  contactName: formData.contactName.trim(),
  
  address: formData.address.trim(),
  city: formData.city.trim(),
  state: formData.state.toUpperCase().trim(),
  zip: formData.zip.trim(),
  
  // Default location for deliveries
  defaultLocation: {
    address: formData.address.trim(),
    city: formData.city.trim(),
    state: formData.state.toUpperCase().trim(),
    zip: formData.zip.trim(),
  },
  
  // Stats
  totalLoadsPosted: 0,
  totalLoadsCompleted: 0,
  averageRating: null,
  totalRatings: 0,
  
  // Status
  status: 'active',
  emailVerified: false,
  
  // Timestamps
  createdAt: serverTimestamp(),
  updatedAt: serverTimestamp(),
});
      
      // Small delay to ensure Firestore writes complete before App.js reads them
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Success - redirect to dealer dashboard
      navigate('/dealer');
      
    } catch (error) {
      console.error('Registration error:', error);
      if (error.code === 'auth/email-already-in-use') {
        setError('This email is already registered. Please login instead.');
      } else {
        setError(error.message || 'Registration failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const field = 'pub-input w-full py-3 px-4 rounded-lg transition';
  const label = 'pub-text block font-semibold mb-2';
  const required = <span className="pub-faint" aria-hidden="true">*</span>;

  return (
    <PublicPage>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {/* Heading */}
        <div className="mb-10">
          <Eyebrow>LiveLoad for dealers</Eyebrow>
          <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold tracking-tight leading-tight text-white">
            Register your dealership
          </h1>
          <p className="mt-4 text-lg text-slate-200">
            Post your auction purchases and get instant bids from verified carriers.
          </p>
        </div>

        {/* Form Card */}
        <div className="pub-surface pub-line border rounded-2xl shadow-2xl shadow-black/30 p-8 sm:p-10">
          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-8" aria-label={`Step ${step} of 3`}>
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center font-bold ${
                  step >= s
                    ? 'pub-btn text-white'
                    : 'pub-soft'
                }`}>
                  {step > s ? <i className="fas fa-check"></i> : s}
                </div>
                {s < 3 && (
                  <div className={`w-12 sm:w-16 h-1 rounded ${step > s ? 'pub-btn' : 'pub-soft'}`}></div>
                )}
              </React.Fragment>
            ))}
          </div>

          {error && (
            <div role="alert" className="pub-error px-4 py-3 rounded-lg text-sm font-semibold mb-6">
              <i className="fas fa-circle-exclamation mr-2"></i>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Step 1: Business Information */}
            {step === 1 && (
              <div className="space-y-5">
                <h2 className="pub-text text-xl font-bold mb-6">
                  <i className="fas fa-building pub-icon mr-2"></i>
                  Business details
                </h2>

                <div>
                  <label htmlFor="dealer-businessName" className={label}>
                    Dealership name {required}
                  </label>
                  <input
                    id="dealer-businessName"
                    type="text"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleInputChange}
                    className={field}
                    placeholder="ABC Auto Sales"
                  />
                </div>

                <div>
                  <label htmlFor="dealer-dealerLicenseNumber" className={label}>
                    Dealer license number {required}
                  </label>
                  <input
                    id="dealer-dealerLicenseNumber"
                    type="text"
                    name="dealerLicenseNumber"
                    value={formData.dealerLicenseNumber}
                    onChange={handleInputChange}
                    className={field}
                    placeholder="DL-123456"
                  />
                  <p className="pub-faint text-sm mt-2">The license number issued by your state.</p>
                </div>

                <div>
                  <label htmlFor="dealer-ein" className={label}>
                    EIN / Tax ID <span className="pub-faint font-normal">(optional)</span>
                  </label>
                  <input
                    id="dealer-ein"
                    type="text"
                    name="ein"
                    value={formData.ein}
                    onChange={handleInputChange}
                    className={field}
                    placeholder="XX-XXXXXXX"
                  />
                </div>
              </div>
            )}

            {/* Step 2: Contact Information */}
            {step === 2 && (
              <div className="space-y-5">
                <h2 className="pub-text text-xl font-bold mb-6">
                  <i className="fas fa-user pub-icon mr-2"></i>
                  Contact and login
                </h2>

                <div>
                  <label htmlFor="dealer-contactName" className={label}>
                    Contact name {required}
                  </label>
                  <input
                    id="dealer-contactName"
                    type="text"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleInputChange}
                    className={field}
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label htmlFor="dealer-email" className={label}>
                    Email {required}
                  </label>
                  <input
                    id="dealer-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={field}
                    placeholder="john@abcauto.com"
                  />
                </div>

                <div>
                  <label htmlFor="dealer-phone" className={label}>
                    Phone {required}
                  </label>
                  <input
                    id="dealer-phone"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={field}
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-5 sm:gap-4">
                  <div>
                    <label htmlFor="dealer-password" className={label}>
                      Password {required}
                    </label>
                    <input
                      id="dealer-password"
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className={field}
                      placeholder="••••••••"
                    />
                  </div>
                  <div>
                    <label htmlFor="dealer-confirmPassword" className={label}>
                      Confirm password {required}
                    </label>
                    <input
                      id="dealer-confirmPassword"
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className={field}
                      placeholder="••••••••"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Address & Agreement */}
            {step === 3 && (
              <div className="space-y-5">
                <h2 className="pub-text text-xl font-bold mb-6">
                  <i className="fas fa-location-dot pub-icon mr-2"></i>
                  Dealership address
                </h2>

                <div>
                  <label htmlFor="dealer-address" className={label}>
                    Street address
                  </label>
                  <input
                    id="dealer-address"
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className={field}
                    placeholder="123 Main Street"
                  />
                </div>

                <div className="grid grid-cols-6 gap-4">
                  <div className="col-span-6 sm:col-span-3">
                    <label htmlFor="dealer-city" className={label}>
                      City {required}
                    </label>
                    <input
                      id="dealer-city"
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className={field}
                      placeholder="Detroit"
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label htmlFor="dealer-state" className={label}>
                      State {required}
                    </label>
                    <input
                      id="dealer-state"
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      maxLength={2}
                      className={`${field} uppercase`}
                      placeholder="MI"
                    />
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <label htmlFor="dealer-zip" className={label}>
                      ZIP {required}
                    </label>
                    <input
                      id="dealer-zip"
                      type="text"
                      name="zip"
                      value={formData.zip}
                      onChange={handleInputChange}
                      className={field}
                      placeholder="48201"
                    />
                  </div>
                </div>

                <div className="pub-surface-2 pub-line border rounded-lg p-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="agreeToTerms"
                      checked={formData.agreeToTerms}
                      onChange={handleInputChange}
                      className="pub-check h-5 w-5 shrink-0 rounded mt-0.5"
                    />
                    <span className="pub-muted text-sm leading-relaxed">
                      I agree to the <a href="/terms" className="pub-link font-medium hover:underline">Terms of Service</a> and <a href="/privacy" className="pub-link font-medium hover:underline">Privacy Policy</a>. I confirm that I am a licensed dealer authorized to purchase and transport vehicles.
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="pub-line border-t flex flex-col-reverse sm:flex-row sm:justify-between gap-3 mt-8 pt-6">
              {step > 1 ? (
                <button
                  type="button"
                  onClick={prevStep}
                  className="pub-line border pub-text px-6 py-3.5 rounded-lg font-semibold hover:opacity-80 transition"
                >
                  <i className="fas fa-arrow-left mr-2"></i>Back
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="pub-line border pub-text px-6 py-3.5 rounded-lg font-semibold hover:opacity-80 transition"
                >
                  Cancel
                </button>
              )}

              {step < 3 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="px-8 py-3.5 pub-btn rounded-lg font-semibold transition"
                >
                  Continue<i className="fas fa-arrow-right ml-2"></i>
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-8 py-3.5 pub-btn rounded-lg font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <><i className="fas fa-spinner fa-spin mr-2"></i>Creating account…</>
                  ) : (
                    <>Create account<i className="fas fa-arrow-right ml-2"></i></>
                  )}
                </button>
              )}
            </div>
          </form>

          {/* Already have account */}
          <div className="pub-line pub-muted border-t mt-7 pt-5 text-center">
            Already have a dealer account?{' '}
            <button
              onClick={() => navigate('/')}
              className="pub-link font-semibold"
            >
              Sign in
            </button>
          </div>
        </div>

        {/* Benefits */}
        <div className="mt-12 grid sm:grid-cols-3 gap-8 sm:gap-6">
          <div className="text-center">
            <div className="w-12 h-12 rounded-full border-2 pub-accent-border flex items-center justify-center mx-auto mb-3">
              <i className="fas fa-bolt pub-accent"></i>
            </div>
            <h4 className="text-white font-bold">Fast setup</h4>
            <p className="text-slate-300 text-sm mt-1">Start posting in minutes</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 rounded-full border-2 pub-accent-border flex items-center justify-center mx-auto mb-3">
              <i className="fas fa-dollar-sign pub-accent"></i>
            </div>
            <h4 className="text-white font-bold">No broker fees</h4>
            <p className="text-slate-300 text-sm mt-1">Only a 5% platform fee</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 rounded-full border-2 pub-accent-border flex items-center justify-center mx-auto mb-3">
              <i className="fas fa-shield-halved pub-accent"></i>
            </div>
            <h4 className="text-white font-bold">Verified carriers</h4>
            <p className="text-slate-300 text-sm mt-1">Carrier insurance is checked</p>
          </div>
        </div>
      </div>
    </PublicPage>
  );
};

export default DealerRegistration